import type { EmpreinteFiche, ProspectFiche, SourceFiche } from "@sfs/contracts";

/** Un « non » se lit mieux en rouge : c'est là que se trouve l'angle de vente. */
function OuiNon({ valeur, inverse = false }: { valeur?: boolean | null; inverse?: boolean }) {
  if (valeur === null || valeur === undefined) return <>—</>;
  const alarmant = inverse ? valeur : !valeur;
  return <span className={alarmant ? "rouge" : undefined}>{valeur ? "oui" : "non"}</span>;
}

export function Annexes({ prospect, empreinte, sources }:
  { prospect: ProspectFiche; empreinte?: EmpreinteFiche | null; sources?: SourceFiche[] }) {
  const interlocuteurs = prospect.interlocuteurs ?? [];
  const implantations = prospect.implantations ?? [];
  const pages = sources ?? [];
  const dateCollecte = empreinte?.scrapeRun?.creeLe?.slice(0, 10);

  return (
    <>
      <section className="panel">
        <h2 className="blocktitle">Qui appeler</h2>
        {interlocuteurs.length === 0 ? (
          <p className="etat">
            Aucune personne nommée sur le site public. Demander le nom du décideur au standard
            avant d'écrire : un mail sans destinataire nommé n'arrive nulle part.
          </p>
        ) : (
          <table>
            <thead><tr><th>Personne</th><th>Rôle</th><th>Angle</th></tr></thead>
            <tbody>
              {interlocuteurs.map(i => (
                <tr key={i.id}>
                  <td><strong>{i.prenom} {i.nom ?? ""}</strong><br /><span className="mut petit">{i.fonction}</span></td>
                  <td><span className="pill">{i.roleVente}</span></td>
                  <td>{i.angleAccroche ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel">
        <h2 className="blocktitle">Identité légale</h2>
        {prospect.siren ? (
          <dl className="dl">
            <dt>Forme</dt><dd>{prospect.formeJuridique ?? "—"}</dd>
            <dt>SIREN</dt><dd>{prospect.siren}</dd>
            <dt>SIRET siège</dt><dd>{prospect.siretSiege ?? "—"}</dd>
            <dt>Créée le</dt><dd>{prospect.dateCreation?.slice(0, 10) ?? "—"}</dd>
            <dt>Établissements</dt><dd>{prospect.nbEtablissements ?? "—"}</dd>
            <dt>Certifications</dt><dd>{(prospect.certifications ?? []).join(" · ") || "—"}</dd>
          </dl>
        ) : (
          <p className="etat">
            Identité légale non rapprochée : aucun SIREN publié sur le site, et aucune
            correspondance suffisamment sûre dans l'annuaire des entreprises. Un rapprochement
            approximatif vaudrait moins que ce vide.
          </p>
        )}
      </section>

      <section className="panel">
        <h2 className="blocktitle">Empreinte technique</h2>
        {empreinte ? (
          <dl className="dl">
            <dt>CMS</dt><dd>{[empreinte.cms, ...(empreinte.technosDetectees ?? [])].filter(Boolean).join(" · ") || "—"}</dd>
            <dt>Agence en place</dt>
            <dd>{empreinte.agenceWeb ? <><strong>{empreinte.agenceWeb}</strong> — concurrent</> : "aucune détectée"}</dd>
            <dt>HTTPS</dt><dd><OuiNon valeur={empreinte.https} /></dd>
            <dt>Paiement en ligne</dt><dd><OuiNon valeur={empreinte.paiementEnLigne} /></dd>
            <dt>Espace client</dt><dd><OuiNon valeur={empreinte.espaceClient} /></dd>
            <dt>Prise de rendez-vous</dt><dd><OuiNon valeur={empreinte.priseRdvEnLigne} /></dd>
            <dt>Chat</dt><dd><OuiNon valeur={empreinte.chatOuChatbot} /></dd>
            <dt>Traceurs marketing</dt><dd>{(empreinte.tagsMarketing ?? []).join(" · ") || "aucun"}</dd>
            <dt>Bandeau cookies</dt><dd><OuiNon valeur={empreinte.cmpCookies} /></dd>
            <dt>Page RGPD</dt><dd><OuiNon valeur={empreinte.pageRgpd} /></dd>
            <dt>Amplitude standard</dt>
            <dd>{empreinte.amplitudeHebdoHeures ? `${empreinte.amplitudeHebdoHeures} h par semaine` : "non publiée"}</dd>
            <dt>Copyright</dt><dd>{empreinte.anneeCopyright ?? "—"}</dd>
            <dt>Langues</dt><dd>{(empreinte.langues ?? []).join(" · ") || "—"}</dd>
          </dl>
        ) : <p className="etat">Aucun relevé technique : le site n'a pas encore été analysé.</p>}
      </section>

      <section className="panel">
        <h2 className="blocktitle">Implantations</h2>
        {implantations.length === 0 ? (
          <p className="etat">Aucune implantation secondaire identifiée sur le site.</p>
        ) : (
          <table>
            <thead><tr><th>Site</th><th>Ville</th><th>Numéro propre</th></tr></thead>
            <tbody>
              {implantations.map(i => (
                <tr key={i.id}>
                  <td>{i.libelle}</td><td>{i.ville ?? "—"}</td>
                  <td>{i.telephonePropre ? "oui" : <span className="rouge">non — renvoi au standard</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel">
        <h2 className="blocktitle">Sources — {pages.length} page(s)</h2>
        <p className="mut petit">
          {dateCollecte ? `Collecte du ${dateCollecte}. ` : ""}
          Sources publiques uniquement : aucun contournement d'authentification, rien issu de LinkedIn.
        </p>
        <ul className="sources">
          {pages.map(s => (
            <li key={s.id}>
              <a href={s.url} target="_blank" rel="noopener noreferrer">{s.url}</a>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
